import { ReactNode, useRef } from 'react'
import { motion, useMotionValue, useSpring, useReducedMotion } from 'framer-motion'

type TiltProps = {
  children: ReactNode
  /** Max tilt in degrees. */
  max?: number
  className?: string
}

// 3D tilt-on-hover wrapper (the mockup's data-tilt cards): the card rotates
// toward the cursor and springs flat on leave. Inert on touch / reduced motion.
const Tilt = ({ children, max = 12, className }: TiltProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const reduce = useReducedMotion()
  const rx = useMotionValue(0)
  const ry = useMotionValue(0)
  const srx = useSpring(rx, { stiffness: 200, damping: 16 })
  const sry = useSpring(ry, { stiffness: 200, damping: 16 })

  const finePointer =
    typeof window !== 'undefined' &&
    window.matchMedia('(pointer: fine)').matches

  if (reduce || !finePointer) {
    return <div className={className}>{children}</div>
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{
        rotateX: srx,
        rotateY: sry,
        transformPerspective: 800,
        transformStyle: 'preserve-3d',
      }}
      onMouseMove={(e) => {
        const r = ref.current?.getBoundingClientRect()
        if (!r) return
        const px = (e.clientX - r.left) / r.width - 0.5
        const py = (e.clientY - r.top) / r.height - 0.5
        ry.set(px * max)
        rx.set(-py * max)
      }}
      onMouseLeave={() => {
        rx.set(0)
        ry.set(0)
      }}
    >
      {children}
    </motion.div>
  )
}

export default Tilt
